import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { pencilIcon } from '@progress/kendo-svg-icons';
import { FC, useEffect, useMemo, useState } from 'react';
import { useHasRole } from '../../hooks';
import { ILocationSubscription, IPaginationResponse } from '../../model';
import { Roles } from '../../model/common/Roles';
import { locationService } from '../../services';
import EditSubscriptionCapacityDialog from './EditSubscriptionCapacityDialog';
import LocationSubscriptionDetails from './LocationSubscriptionDetails';

import styles from './LocationDetails.module.scss';

const EDIT_ROLES: Roles[] = ['AppAdmin'];

const PAGE_SIZES = [5, 10, 15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

interface IPageState {
  skip: number;
  take: number;
}

// a grid row: the subscription with the detail row state
interface ISubscriptionRow extends ILocationSubscription {
  locationId: string;
  expanded: boolean;
}

// declared outside of the component: the grid remounts the detail rows (and reloads the details) if the component type changes
const SubscriptionDetailRow: FC<GridDetailRowProps> = ({ dataItem }) => {
  const row = dataItem as ISubscriptionRow;
  return <LocationSubscriptionDetails locationId={row.locationId} subscriptionId={row.id} />;
};

interface Props {
  locationId: string;
}

const LocationSubscriptions: FC<Props> = ({ locationId }) => {
  const canEdit = useHasRole(EDIT_ROLES);

  const [pageState, setPageState] = useState<IPageState>({ skip: 0, take: DEFAULT_PAGE_SIZE });
  const [subscriptions, setSubscriptions] = useState<IPaginationResponse<ILocationSubscription>>();
  const [isLoading, setIsLoading] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<ILocationSubscription | null>(null);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  useEffect(() => {
    let isActual = true;
    setIsLoading(true);
    locationService
      .getLocationSubscriptions(locationId, pageState.skip / pageState.take + 1, pageState.take)
      .then((response) => isActual && setSubscriptions(response))
      .catch(() => isActual && setSubscriptions(undefined))
      .finally(() => isActual && setIsLoading(false));

    return () => {
      isActual = false;
    };
  }, [locationId, pageState]);

  const rows = useMemo(
    (): ISubscriptionRow[] =>
      (subscriptions?.data || []).map((x) => ({ ...x, locationId, expanded: expandedIds.includes(x.id) })),
    [subscriptions, locationId, expandedIds],
  );

  const handleExpandChange = (event: GridExpandChangeEvent) => {
    const id = (event.dataItem as ISubscriptionRow).id;
    setExpandedIds((prev) => (event.value ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handlePageChange = (event: GridPageChangeEvent) => setPageState({ skip: event.page.skip, take: event.page.take });

  const handleSaveCapacity = async (capacity: number) => {
    if (!editingSubscription) return;

    const updated = await locationService.updateLocationSubscriptionCapacity(locationId, editingSubscription.id, capacity);
    setSubscriptions((prev) => prev && { ...prev, data: prev.data.map((item) => (item.id === updated.id ? updated : item)) });
    setEditingSubscription(null);
  };

  const ActionsCell: FC<GridCellProps> = ({ dataItem, className, style }) => (
    <td className={`${className ?? ''} ${styles.actionsCell}`} style={style}>
      <Button
        svgIcon={pencilIcon}
        fillMode='flat'
        className={styles.actionButton}
        title='Edit'
        onClick={() => setEditingSubscription(dataItem as ILocationSubscription)}
      />
    </td>
  );

  return (
    <>
      <Grid
        data={rows}
        dataItemKey='id'
        detail={SubscriptionDetailRow}
        expandField='expanded'
        onExpandChange={handleExpandChange}
        total={subscriptions?.totalCount || 0}
        skip={pageState.skip}
        take={pageState.take}
        pageable={{ buttonCount: 5, info: true, pageSizes: PAGE_SIZES }}
        onPageChange={handlePageChange}
        style={{ opacity: isLoading ? 0.6 : 1 }}
        className={`${styles.subscriptionsGrid} grid-auto-scroll`}
      >
        <GridColumn field='subscriptionId' title='Subscription ID' />
        <GridColumn field='subscriptionName' title='Subscription Name' />
        <GridColumn field='capacity' title='Capacity' width={120} />
        {canEdit && <GridColumn title='Actions' width={100} cell={ActionsCell} />}
      </Grid>

      {canEdit && editingSubscription && (
        <EditSubscriptionCapacityDialog
          subscription={editingSubscription}
          onClose={() => setEditingSubscription(null)}
          onSave={handleSaveCapacity}
        />
      )}
    </>
  );
};

export default LocationSubscriptions;
